import { z } from "zod";

import type { CloudApi, SyncRequest, SyncResponse } from "./cloud-repository";
import { CLOUD_COLLECTIONS } from "./state-codec";

const MAX_RESPONSE_BYTES = 8 * 1024 * 1024;

const serverChangeSchema = z.object({
  ownerUid: z.string().min(1).max(128),
  collection: z.enum(CLOUD_COLLECTIONS),
  recordId: z.string().min(1).max(256),
  payload: z.unknown().nullable(),
  clientUpdatedAt: z.number().int().nonnegative(),
  contentHash: z.string().min(1).max(160),
  deletedAt: z.number().int().nonnegative().nullable(),
  serverRevision: z.number().int().nonnegative(),
});

const syncResponseSchema = z.object({
  cursor: z.number().int().nonnegative(),
  acceptedMutationIds: z.array(z.string().min(1).max(128)),
  changes: z.array(serverChangeSchema),
  conflicts: z.array(z.record(z.string(), z.unknown())),
  hasMore: z.boolean(),
  serverTime: z.number().int().nonnegative(),
  minimumClientBuild: z.string().min(1).max(128),
});

const driveStatusSchema = z.discriminatedUnion("connected", [
  z.object({ connected: z.literal(false) }),
  z.object({
    connected: z.literal(true),
    scopes: z.array(z.string()),
    folders: z.object({
      rootId: z.string(),
      photosId: z.string(),
      attachmentsId: z.string(),
      backupsId: z.string(),
    }),
  }),
]);

const driveStartSchema = z.object({ authorizationUrl: z.string().url() });

const cloudAssetSchema = z.object({
  ownerUid: z.string(),
  assetId: z.string(),
  collection: z.string().nullable(),
  recordId: z.string().nullable(),
  driveFileId: z.string().nullable(),
  kind: z.enum(["photo", "attachment", "backup"]),
  sanitizedName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  checksum: z.string(),
  createdAt: z.number(),
  updatedAt: z.number(),
  deletedAt: z.number().nullable(),
});

const snapshotSchema = z.object({
  backupId: z.string().uuid(),
  kind: z.enum(["manual", "automatic", "pre_restore"]),
  sourceRevision: z.number().int().nonnegative(),
  checksum: z.string().regex(/^sha256:[0-9a-f]{64}$/u),
  sizeBytes: z.number().int().nonnegative(),
  createdAt: z.number().int().nonnegative(),
});

export type CloudSnapshot = z.infer<typeof snapshotSchema>;

export type DriveStatus = z.infer<typeof driveStatusSchema>;
export type CloudAsset = z.infer<typeof cloudAssetSchema>;
export type CloudAssetUpload = Readonly<{
  assetId: string;
  kind: "photo" | "attachment" | "backup";
  name: string;
  collection: string | null;
  recordId: string | null;
  mimeType: string;
  checksum: string;
  blob: Blob;
}>;

async function boundedJson(response: Response): Promise<unknown> {
  if (response.body === null) throw new Error("Cloud response is invalid");
  const declaredLength = Number(response.headers.get("Content-Length"));
  if (Number.isFinite(declaredLength) && declaredLength > MAX_RESPONSE_BYTES) {
    throw new Error("Cloud response exceeds the client safety limit");
  }

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  while (true) {
    const chunk = await reader.read();
    if (chunk.done) break;
    total += chunk.value.byteLength;
    if (total > MAX_RESPONSE_BYTES) {
      await reader.cancel();
      throw new Error("Cloud response exceeds the client safety limit");
    }
    chunks.push(chunk.value);
  }
  const bytes = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  try {
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    throw new Error("Cloud response is invalid JSON");
  }
}

export class FirebaseCloudApi implements CloudApi {
  private readonly baseUrl: string;

  constructor(
    baseUrl: string,
    private readonly getIdToken: () => Promise<string>,
    private readonly fetcher: typeof fetch = (...args) => globalThis.fetch(...args),
  ) {
    this.baseUrl = baseUrl.replace(/\/+$/, "");
    if (!/^https?:\/\//.test(this.baseUrl)) {
      throw new TypeError("Cloud API URL must be absolute");
    }
  }

  async sync(request: SyncRequest): Promise<SyncResponse> {
    const response = await this.authorizedFetch("/v1/sync", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      response.body?.cancel().catch(() => undefined);
      throw new Error(`Cloud sync failed (${response.status})`);
    }

    const parsed = syncResponseSchema.safeParse(await boundedJson(response));
    if (!parsed.success) throw new Error("Cloud sync response is invalid");
    return parsed.data;
  }

  private async authorizedFetch(
    path: string,
    init: RequestInit = {},
    timeoutMs = 20_000,
  ): Promise<Response> {
    const token = await this.getIdToken();
    const headers = new Headers(init.headers);
    headers.set("Authorization", `Bearer ${token}`);
    return this.fetcher(`${this.baseUrl}${path}`, {
      ...init,
      credentials: "omit",
      headers,
      signal: init.signal ?? AbortSignal.timeout(timeoutMs),
    });
  }

  eventSocketUrl(): string {
    const url = new URL(`${this.baseUrl}/v1/events`);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    return url.toString();
  }

  async driveStatus(): Promise<DriveStatus> {
    const response = await this.authorizedFetch("/v1/drive/status");
    if (!response.ok) throw new Error(`Drive status failed (${response.status})`);
    const parsed = driveStatusSchema.safeParse(await boundedJson(response));
    if (!parsed.success) throw new Error("Drive status response is invalid");
    return parsed.data;
  }

  async startDriveOAuth(): Promise<string> {
    const response = await this.authorizedFetch("/v1/drive/oauth/start");
    if (!response.ok) throw new Error(`Drive connection failed (${response.status})`);
    const parsed = driveStartSchema.safeParse(await boundedJson(response));
    if (!parsed.success) throw new Error("Drive authorization response is invalid");
    return parsed.data.authorizationUrl;
  }

  async disconnectDrive(): Promise<void> {
    const response = await this.authorizedFetch("/v1/drive", { method: "DELETE" });
    if (!response.ok) throw new Error(`Drive disconnection failed (${response.status})`);
    await response.body?.cancel();
  }

  async uploadAsset(input: CloudAssetUpload): Promise<CloudAsset> {
    const checksum = input.checksum.startsWith("sha256:")
      ? input.checksum.toLowerCase()
      : `sha256:${input.checksum.toLowerCase()}`;
    if (!/^sha256:[0-9a-f]{64}$/u.test(checksum)) {
      throw new TypeError("Asset checksum must be a SHA-256 digest");
    }
    const query = new URLSearchParams({
      kind: input.kind,
      name: input.name,
      checksum,
    });
    if (input.collection !== null) query.set("collection", input.collection);
    if (input.recordId !== null) query.set("recordId", input.recordId);
    const response = await this.authorizedFetch(
      `/v1/assets/${encodeURIComponent(input.assetId)}?${query.toString()}`,
      {
        method: "POST",
        headers: { "Content-Type": input.mimeType },
        body: input.blob,
      },
      60_000,
    );
    if (!response.ok) throw new Error(`Asset upload failed (${response.status})`);
    const parsed = cloudAssetSchema.safeParse(await boundedJson(response));
    if (!parsed.success) throw new Error("Asset upload response is invalid");
    return parsed.data;
  }

  async downloadAsset(assetId: string): Promise<Blob> {
    const response = await this.authorizedFetch(
      `/v1/assets/${encodeURIComponent(assetId)}`,
      {},
      60_000,
    );
    if (!response.ok) throw new Error(`Asset download failed (${response.status})`);
    const declared = Number(response.headers.get("Content-Length"));
    if (Number.isFinite(declared) && declared > 10 * 1024 * 1024) {
      await response.body?.cancel();
      throw new Error("Asset download exceeds the safety limit");
    }
    const blob = await response.blob();
    if (blob.size > 10 * 1024 * 1024) throw new Error("Asset download exceeds the safety limit");
    return blob;
  }

  async deleteAsset(assetId: string): Promise<void> {
    const response = await this.authorizedFetch(
      `/v1/assets/${encodeURIComponent(assetId)}`,
      { method: "DELETE" },
    );
    if (!response.ok) throw new Error(`Asset deletion failed (${response.status})`);
    await response.body?.cancel();
  }

  async restoreAsset(assetId: string): Promise<void> {
    const response = await this.authorizedFetch(
      `/v1/assets/${encodeURIComponent(assetId)}/restore`,
      { method: "POST" },
    );
    if (!response.ok) throw new Error(`Asset restore failed (${response.status})`);
    await response.body?.cancel();
  }

  async listBackups(): Promise<CloudSnapshot[]> {
    const response = await this.authorizedFetch("/v1/backups");
    if (!response.ok) throw new Error(`Backup listing failed (${response.status})`);
    const parsed = z.array(snapshotSchema).safeParse(await boundedJson(response));
    if (!parsed.success) throw new Error("Backup listing response is invalid");
    return parsed.data;
  }

  async createBackup(kind: "manual" | "automatic" = "manual"): Promise<CloudSnapshot> {
    const response = await this.authorizedFetch(
      `/v1/backups?kind=${encodeURIComponent(kind)}`,
      { method: "POST" },
      60_000,
    );
    if (!response.ok) throw new Error(`Backup creation failed (${response.status})`);
    const parsed = snapshotSchema.safeParse(await boundedJson(response));
    if (!parsed.success) throw new Error("Backup creation response is invalid");
    return parsed.data;
  }

  async restoreBackup(backupId: string): Promise<number> {
    const response = await this.authorizedFetch(
      `/v1/backups/${encodeURIComponent(backupId)}/restore`,
      { method: "POST" },
      60_000,
    );
    if (!response.ok) throw new Error(`Backup restore failed (${response.status})`);
    const parsed = z
      .object({ revision: z.number().int().nonnegative() })
      .safeParse(await boundedJson(response));
    if (!parsed.success) throw new Error("Backup restore response is invalid");
    return parsed.data.revision;
  }
}
